import { z } from 'zod';

/**
 * Mínimo de chars do motivo de cancelamento.
 *
 * Antecipa client-side a rule do proc upstream (`Cancelar` → 400 `VALIDACAO`
 * quando `&motivo.Trim().Length < 10`), documentada em
 * `kb-exports/empresa/ApiEmpresaNotasFiscais.spec.md` §5 (linhas 225/267) e
 * confirmada no smoke da Fase B (2026-06-23).
 */
export const MOTIVO_MIN_LENGTH = 10;
export const MOTIVO_MAX_LENGTH = 200;

const modeloSchema = z.string().trim().min(1, 'Modelo obrigatório').max(20, 'Máximo 20 caracteres');
const serieSchema = z.string().trim().min(1, 'Série obrigatória').max(20, 'Máximo 20 caracteres');

export const companyNotaFiscalGerarAutomaticaSchema = z.object({
  modelo: modeloSchema,
  serie: serieSchema,
  colaboradorCodigo: z
    .number({ invalid_type_error: 'Selecione um colaborador' })
    .int('Use número inteiro')
    .positive('Selecione um colaborador'),
  colaboradorNotaFiscalCodigo: z
    .number({ invalid_type_error: 'Selecione a NF do colaborador' })
    .int('Use número inteiro')
    .positive('Selecione a NF do colaborador'),
});

export const companyNotaFiscalGerarManualSchema = companyNotaFiscalGerarAutomaticaSchema
  .extend({
    notaMaeEmpresaCodigo: z
      .number({ invalid_type_error: 'Valor inválido' })
      .int('Use número inteiro')
      .positive('Deve ser maior que zero')
      .nullish()
      .transform((v) => (v == null ? null : v)),
    notaMaeEmpresaNotaFiscalCodigo: z
      .number({ invalid_type_error: 'Valor inválido' })
      .int('Use número inteiro')
      .positive('Deve ser maior que zero')
      .nullish()
      .transform((v) => (v == null ? null : v)),
  })
  // Regra: notaMae* viaja em par — ou ambos preenchidos, ou ambos vazios.
  .superRefine((val, ctx) => {
    const hasEmp = val.notaMaeEmpresaCodigo != null;
    const hasNf = val.notaMaeEmpresaNotaFiscalCodigo != null;
    if (hasEmp !== hasNf) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasEmp ? 'notaMaeEmpresaNotaFiscalCodigo' : 'notaMaeEmpresaCodigo'],
        message: 'Informe os dois campos da nota-mãe ou deixe ambos em branco.',
      });
    }
  });

export const companyNotaFiscalCancelarSchema = z.object({
  motivo: z
    .string()
    .trim()
    .min(MOTIVO_MIN_LENGTH, `Motivo deve ter pelo menos ${MOTIVO_MIN_LENGTH} caracteres`)
    .max(MOTIVO_MAX_LENGTH, `Máximo ${MOTIVO_MAX_LENGTH} caracteres`),
});

export type CompanyNotaFiscalGerarAutomaticaValues = z.input<
  typeof companyNotaFiscalGerarAutomaticaSchema
>;
export type CompanyNotaFiscalGerarManualValues = z.input<typeof companyNotaFiscalGerarManualSchema>;
export type CompanyNotaFiscalCancelarValues = z.input<typeof companyNotaFiscalCancelarSchema>;
